import React from "react";
import { Button, Card } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { groupEwbNo } from "./ewayBillUtils";

// Standard GSTIN layout (2-digit state + PAN + entity + 'Z' + check digit).
const GSTIN_REGEX = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/;
// What the NIC e-Way API actually accepts (looser — covers sandbox/test GSTINs like ...Q000).
const GSTIN_API_REGEX = /^[0-9]{2}[0-9A-Z]{13}$/;
const PIN_REGEX = /^[1-9][0-9]{5}$/;

/**
 * Predict whether the server-side auto e-Way bill generation will succeed for
 * this sale, using the same rules the government portal enforces.
 * `shipTo` is the customer's selected shipping address ({ address, pinCode }),
 * or null when the load ships to the billing address.
 * Each check: { level: 'ok' | 'warn' | 'fail', label }
 */
export function buildEwayChecks({ org, customer, values, ticketNumber, shipTo }) {
    const checks = [];
    const add = (level, label) => checks.push({ level, label });

    // Seller (organization)
    const orgGstin = String(org?.taxId || "").trim().toUpperCase();
    if (!org) add("warn", "Organization details not synced yet — GSTIN cannot be verified");
    else if (!orgGstin) add("fail", "Your organization GSTIN (Tax ID) is not set — ask your admin to add it on the server");
    else if (!GSTIN_API_REGEX.test(orgGstin)) add("fail", "Your organization GSTIN looks invalid");
    else if (!GSTIN_REGEX.test(orgGstin)) add("warn", `Your GSTIN ${orgGstin} has an unusual format (test GSTIN?) — the portal decides`);
    else add("ok", `Your GSTIN ${orgGstin}`);
    if (org && !PIN_REGEX.test(String(org.pinCode || ""))) add("fail", "Organization pin code is missing/invalid (6 digits required)");

    // Buyer (customer)
    if (!values.salesCustomer) {
        add("fail", "Select a customer");
    } else if (!customer) {
        add("warn", "Loading customer details…");
    } else {
        const custGstin = String(customer.gstNumber || "").trim().toUpperCase();
        if (!custGstin) add("warn", "Customer has no GSTIN — bill goes out as URP (unregistered person)");
        else if (!GSTIN_API_REGEX.test(custGstin)) add("fail", "Customer GSTIN looks invalid — fix it on the customer record");
        else if (!GSTIN_REGEX.test(custGstin)) add("warn", `Customer GSTIN ${custGstin} has an unusual format (test GSTIN?) — the portal decides`);
        else add("ok", `Customer GSTIN ${custGstin}`);

        // The delivery pin code is the selected ship-to address's when one is chosen,
        // otherwise the customer's own (billing) pin code.
        if (shipTo) {
            if (!PIN_REGEX.test(String(shipTo.pinCode || ""))) add("fail", "Ship-to pin code is missing/invalid — the portal requires the delivery pin code");
            else add("ok", `Ship-to pin code ${shipTo.pinCode}`);

            if (!String(shipTo.address || "").trim()) add("warn", "The selected shipping address has no address text");
        } else {
            if (!PIN_REGEX.test(String(customer.pinCode || ""))) add("fail", "Customer pin code is missing/invalid — the portal requires the delivery pin code");
            else add("ok", `Customer pin code ${customer.pinCode}`);

            if (!String(customer.address || "").trim()) add("warn", "Customer has no address on record");
        }
    }

    // Transport
    const lead = parseFloat(values.leadKm);
    if (!lead || lead <= 0) add("fail", "Lead (km) is required — it becomes the transport distance");
    else if (lead >= 4000) add("fail", "Lead (km) must be below 4000");
    else add("ok", `Transport distance ${Math.round(lead)} km`);

    // The form keeps the selected vehicle as an object; vehicleNumber is only
    // derived at submit time — mirror the submit's resolution order here.
    const rawVehicle = values.vehicle?.name || values.vehicle?.vehicleNumber || values.vehicleNumber || "";
    const vehicle = String(rawVehicle).replace(/[\s-]/g, "");
    if (!vehicle) add("warn", "No vehicle number — bill will be generated without Part-B vehicle details");
    else if (vehicle.length > 10) add("fail", "Vehicle number is too long (max 10 characters)");
    else add("ok", `Vehicle ${vehicle.toUpperCase()}`);

    // Items
    const items = values.itemsList || [];
    if (items.length === 0) {
        add("fail", "Add at least one item");
    } else {
        const badHsn = items.filter((it) => String(it.hsnCode || "").replace(/\D/g, "").length < 6);
        if (badHsn.length === 0) add("ok", "All item HSN codes are 6+ digits");
        badHsn.forEach((it) => add("fail", `"${it.inventoryItemName || "Item"}": HSN code must be at least 6 digits`));
        items.filter((it) => !(parseFloat(it.quantity) > 0))
            .forEach((it) => add("fail", `"${it.inventoryItemName || "Item"}": quantity must be above zero`));
    }

    // Document number
    if (ticketNumber && String(ticketNumber).trim().length > 16) {
        add("fail", "Ticket number is longer than the 16 characters allowed on an e-Way bill");
    }

    return checks;
}

const ICONS = {
    ok: <i className="bi bi-check-circle-fill text-success me-2"></i>,
    warn: <i className="bi bi-exclamation-triangle-fill text-warning me-2"></i>,
    fail: <i className="bi bi-x-circle-fill text-danger me-2"></i>,
};

/** Live readiness card shown below Bill Summary when Generate E-Way Bill is on. */
export function EwayBillReadinessPanel({ org, customer, values, ticketNumber, shipTo }) {
    const checks = buildEwayChecks({ org, customer, values, ticketNumber, shipTo });
    const failCount = checks.filter((c) => c.level === "fail").length;
    const warnCount = checks.filter((c) => c.level === "warn").length;

    const tone = failCount > 0
        ? { bg: "#fdecec", color: "#d9214e", icon: "bi-x-octagon-fill", title: `E-Way bill will FAIL — fix ${failCount} issue${failCount === 1 ? "" : "s"}` }
        : warnCount > 0
            ? { bg: "#fff8dd", color: "#b58f00", icon: "bi-exclamation-triangle-fill", title: "E-Way bill will generate, with warnings" }
            : { bg: "#e8fff3", color: "#0f9d58", icon: "bi-patch-check-fill", title: "Ready — e-Way bill will be generated when this sale syncs" };

    return (
        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: "8px" }}>
            <div className="px-4 py-2" style={{ borderRadius: "8px 8px 0 0", backgroundColor: tone.bg }}>
                <div className="d-flex align-items-center">
                    <i className={`bi ${tone.icon} me-2`} style={{ color: tone.color }}></i>
                    <span className="fw-semibold small" style={{ color: tone.color }}>E-WAY BILL CHECK — {tone.title}</span>
                </div>
            </div>
            <Card.Body className="p-3">
                {/* fails first, then warnings, ok last */}
                {["fail", "warn", "ok"].map((level) =>
                    checks.filter((c) => c.level === level).map((c, i) => (
                        <div key={level + i} className="d-flex align-items-start small py-1">
                            {ICONS[c.level]}
                            <span className={c.level === "fail" ? "text-danger" : c.level === "warn" ? "text-gray-700" : "text-muted"}>{c.label}</span>
                        </div>
                    ))
                )}
                <div className="text-muted mt-2" style={{ fontSize: "0.72rem" }}>
                    <i className="bi bi-info-circle me-1"></i>
                    Checked on this device — the government portal has the final say when the sale syncs.
                </div>
            </Card.Body>
        </Card>
    );
}

/**
 * Outcome card for a sale that already has an e-Way bill result — replaces the
 * pre-flight checklist once the bill exists. Same card language as the page.
 */
export function EwayBillOutcomeCard({ sale, onOpenDetails }) {
    const navigate = useNavigate();
    const status = String(sale?.ewayBillStatus || "").toUpperCase();
    const tone = status === "GENERATED"
        ? { bg: "#e8fff3", color: "#0f9d58", icon: "bi-patch-check-fill", label: "Generated" }
        : status === "CANCELLED"
            ? { bg: "#f5f6f8", color: "#7e8299", icon: "bi-slash-circle", label: "Cancelled" }
            : { bg: "#fdecec", color: "#d9214e", icon: "bi-x-octagon-fill", label: "Failed" };

    return (
        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: "8px" }}>
            <div className="px-4 py-2" style={{ borderRadius: "8px 8px 0 0", backgroundColor: tone.bg }}>
                <div className="d-flex align-items-center">
                    <i className={`bi ${tone.icon} me-2`} style={{ color: tone.color }}></i>
                    <span className="fw-semibold small" style={{ color: tone.color }}>E-WAY BILL — {tone.label}</span>
                </div>
            </div>
            <Card.Body className="p-3">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                    <div className="d-flex flex-wrap gap-4">
                        {sale?.ewayBillNo && (
                            <div>
                                <div className="text-muted" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.4px" }}>Bill Number</div>
                                <div className={`fw-bold ${status === "CANCELLED" ? "text-muted text-decoration-line-through" : ""}`}
                                    style={{ fontFamily: "monospace", letterSpacing: "1px", color: status === "GENERATED" ? "#0f9d58" : undefined }}>
                                    {groupEwbNo(sale.ewayBillNo)}
                                </div>
                            </div>
                        )}
                        <div>
                            <div className="text-muted" style={{ fontSize: "0.7rem", textTransform: "uppercase", letterSpacing: "0.4px" }}>Server Invoice</div>
                            <div className="fw-semibold small">{sale?.invoiceNumber || "—"}</div>
                        </div>
                    </div>
                    <div className="d-flex gap-2">
                        {status === "GENERATED" && (
                            <Button variant="success" size="sm" onClick={() => navigate(`/eway-print/${sale.id}`)}>
                                <i className="bi bi-printer me-2"></i>View / Print
                            </Button>
                        )}
                        <Button variant="light" size="sm" onClick={onOpenDetails}>
                            <i className="bi bi-three-dots me-2"></i>Details
                        </Button>
                    </div>
                </div>
                {status === "FAILED" && (
                    <div className="small text-danger mt-2" style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        <i className="bi bi-exclamation-octagon me-1"></i>{sale?.ewayBillError || "Generation failed."}
                        <span className="text-muted"> — open Details to fix and regenerate.</span>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}

/**
 * Upsell card shown below Bill Summary for orgs without the GST Services add-on.
 * Matches the page's card language (tinted header strip + light body).
 */
export function EwayUpsellCard() {
    const navigate = useNavigate();
    return (
        <Card className="mb-3 border-0 shadow-sm" style={{ borderRadius: "10px", overflow: "hidden" }}>
            {/* Premium header strip — light gold, fits the page's card language */}
            <div className="px-4 py-2" style={{ backgroundColor: "#fff4d6" }}>
                <span className="d-inline-flex align-items-center"
                    style={{ color: "#b58f00", fontWeight: 700, fontSize: "0.72rem", letterSpacing: "0.5px" }}>
                    <i className="bi bi-gem me-2"></i>PREMIUM — GST SERVICES
                </span>
            </div>
            <Card.Body className="p-4 position-relative overflow-hidden">
                {/* Faint animated e-Way bill watermark for a touch of life */}
                <svg width="58" height="58" viewBox="0 0 60 60" aria-hidden="true"
                    className="position-absolute" style={{ top: 12, right: 14, opacity: 0.13, pointerEvents: "none" }}>
                    <rect x="16" y="12" width="30" height="40" rx="4" fill="none" stroke="#E0A020" strokeWidth="2.5" />
                    <line x1="22" y1="21" x2="40" y2="21" stroke="#E0A020" strokeWidth="2.5" strokeLinecap="round" />
                    <g fill="#E0A020">
                        <rect x="22" y="28" width="6" height="6" rx="1"><animate attributeName="opacity" values="0.3;1;0.3" dur="2.2s" repeatCount="indefinite" /></rect>
                        <rect x="31" y="28" width="6" height="6" rx="1"><animate attributeName="opacity" values="1;0.3;1" dur="2.2s" repeatCount="indefinite" /></rect>
                        <rect x="22" y="37" width="6" height="6" rx="1"><animate attributeName="opacity" values="0.6;1;0.6" dur="2.2s" begin="0.4s" repeatCount="indefinite" /></rect>
                        <rect x="31" y="37" width="6" height="6" rx="1"><animate attributeName="opacity" values="1;0.4;1" dur="2.2s" begin="0.4s" repeatCount="indefinite" /></rect>
                    </g>
                    <g>
                        <circle cx="45" cy="13" r="8" fill="#17C653">
                            <animate attributeName="r" values="0;8;8;8" keyTimes="0;0.3;0.9;1" dur="3s" repeatCount="indefinite" />
                        </circle>
                        <path d="M41.5 13 L44 15.5 L48.5 10.5" stroke="#ffffff" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                    </g>
                </svg>

                {/* Hook — animated stopwatch + the time saved */}
                <div className="d-flex align-items-center mb-3 position-relative">
                    <span className="d-inline-flex align-items-center justify-content-center flex-shrink-0 me-3"
                        style={{ width: 52, height: 52, borderRadius: "50%", background: "#fff4e5" }}>
                        <svg width="34" height="34" viewBox="0 0 48 48" aria-hidden="true">
                            <circle cx="24" cy="26" r="16" fill="none" stroke="#F5B942" strokeWidth="2" opacity="0.4">
                                <animate attributeName="r" values="16;18.5;16" dur="2.4s" repeatCount="indefinite" />
                                <animate attributeName="opacity" values="0.4;0.1;0.4" dur="2.4s" repeatCount="indefinite" />
                            </circle>
                            <circle cx="24" cy="26" r="13" fill="none" stroke="#E0A020" strokeWidth="2.5" />
                            <rect x="20.5" y="6" width="7" height="4" rx="1.5" fill="#E0A020" />
                            <rect x="22.5" y="9" width="3" height="4" fill="#E0A020" />
                            <line x1="24" y1="26" x2="24" y2="16.5" stroke="#2D2B55" strokeWidth="2" strokeLinecap="round">
                                <animateTransform attributeName="transform" type="rotate" from="0 24 26" to="360 24 26" dur="3s" repeatCount="indefinite" />
                            </line>
                            <circle cx="24" cy="26" r="2" fill="#2D2B55" />
                        </svg>
                    </span>
                    <div>
                        <div className="fw-bold text-gray-900" style={{ fontSize: "1.1rem", lineHeight: 1.15 }}>
                            Save ~20 min <span style={{ color: "#E0A020" }}>on every sale</span>
                        </div>
                        <div className="text-muted small">E-Way bills, without the portal</div>
                    </div>
                </div>

                <div className="d-flex flex-wrap gap-2 mb-3">
                    <span className="badge" style={{ background: "#e8fff3", color: "#0f9d58", fontWeight: 600 }}>
                        <i className="bi bi-check-circle-fill me-1"></i>Auto e-Way bill
                    </span>
                    <span className="badge" style={{ background: "#eef0ff", color: "#635BFF", fontWeight: 600 }}>
                        <i className="bi bi-check-circle-fill me-1"></i>No portal login
                    </span>
                    <span className="badge" style={{ background: "#fff4e5", color: "#b58f00", fontWeight: 600 }}>
                        <i className="bi bi-check-circle-fill me-1"></i>No OTP calls
                    </span>
                </div>

                <div className="small text-gray-700" style={{ lineHeight: 1.65 }}>
                    Today, one e-Way bill means logging into the government website, waiting for an OTP on the owner's
                    phone, typing every detail by hand and printing it — close to 20 minutes for each load.
                    With GST Services, RevCon does all of that automatically the moment you save the sale.
                    The operator never leaves this screen and never has to call the owner for a code.
                    Every truck rolls out faster, and nothing is ever typed twice.
                </div>

                <div className="d-flex align-items-center justify-content-between mt-3 flex-wrap gap-2">
                    <span className="small text-muted">
                        <i className="bi bi-telephone me-1"></i>Ask your owner to call{" "}
                        <a href="tel:+919187214264" className="fw-semibold text-nowrap">+91 91872 14264</a>
                    </span>
                    <button type="button" className="btn btn-sm btn-primary" onClick={() => navigate("/gst-services-info")}>
                        See how it works <i className="bi bi-arrow-right ms-1"></i>
                    </button>
                </div>
            </Card.Body>
        </Card>
    );
}
